// Renders a localized legal document (privacy / terms) from the
// structured LegalDoc model: locale-aware nav, per-locale <head> with
// hreflang, and the section/list layout. Links inside text (the support
// email and the ANPD URL) are auto-detected here so the content stays
// plain data.

import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Head } from "vite-react-ssg";

import { LangSwitcher } from "@/components/ui/LangSwitcher";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import {
  LOCALES,
  LOCALE_HREFLANG,
  localePath,
  localeUrl,
  type PageKey,
} from "@/i18n/config";
import { useDict, useLocale } from "@/i18n/LocaleProvider";
import type { LegalDoc } from "@/i18n/legal/types";

const EMAIL = "support@truehacking.ai";
const ANPD_TEXT = "gov.br/anpd";
const ANPD_URL = "https://www.gov.br/anpd";

// Split a string on the known link tokens and return linked nodes.
function autoLink(text: string): ReactNode {
  const parts = text.split(new RegExp(`(${EMAIL}|${ANPD_TEXT})`, "g"));
  return parts.map((part, i) => {
    if (part === EMAIL) {
      return (
        <a key={i} href={`mailto:${EMAIL}`} className="text-accent underline">
          {EMAIL}
        </a>
      );
    }
    if (part === ANPD_TEXT) {
      return (
        <a
          key={i}
          href={ANPD_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent underline"
        >
          {ANPD_TEXT}
        </a>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

function LegalNav({ page }: { page: PageKey }) {
  const locale = useLocale();
  const { footer } = useDict();
  return (
    <nav
      className="flex items-center justify-between px-6 py-4 sticky top-0 z-40"
      style={{
        background: "rgb(var(--color-bg) / 0.92)",
        backdropFilter: "blur(8px)",
        borderBottom: "1px solid rgb(var(--color-stroke))",
      }}
    >
      <Link to={localePath(locale)} className="flex items-center gap-3 no-underline">
        <img src="/logo.png" alt="TrueHacking logo" width={200} height={200} className="h-9 w-auto" />
        <div className="font-display text-[16px] font-bold text-accent tracking-[0.12em]">
          TrueHacking
        </div>
      </Link>
      <div className="flex items-center gap-4 font-mono text-[11px] text-muted">
        <Link
          to={localePath(locale, "privacy")}
          className={page === "privacy" ? "no-underline text-content" : "no-underline text-muted"}
        >
          {footer.privacy}
        </Link>
        <Link
          to={localePath(locale, "terms")}
          className={page === "terms" ? "no-underline text-content" : "no-underline text-muted"}
        >
          {footer.terms}
        </Link>
        <LangSwitcher page={page} className="hidden sm:flex" />
        <ThemeToggle />
      </div>
    </nav>
  );
}

export function LegalDocView({ page, doc }: { page: PageKey; doc: LegalDoc }) {
  const locale = useLocale();
  return (
    <div className="bg-bg min-h-screen text-content">
      <Head>
        <html lang={LOCALE_HREFLANG[locale]} />
        <title>{doc.metaTitle}</title>
        <meta name="description" content={doc.metaDescription} />
        <link rel="canonical" href={localeUrl(locale, page)} />
        <meta property="og:url" content={localeUrl(locale, page)} />
        <meta property="og:title" content={doc.metaTitle} />
        <meta property="og:description" content={doc.metaDescription} />
        {LOCALES.map((loc) => (
          <link key={loc} rel="alternate" hrefLang={LOCALE_HREFLANG[loc]} href={localeUrl(loc, page)} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={localeUrl("pt", page)} />
      </Head>

      <LegalNav page={page} />

      <main className="max-w-[820px] mx-auto px-6 py-12">
        <h1 className="font-display text-[36px] font-bold text-content mb-2">
          {doc.title}
        </h1>
        <div className="font-mono text-[11px] text-muted mb-10">{doc.effective}</div>

        {doc.sections.map((section) => (
          <section key={section.title} className="mb-10">
            <h2 className="font-display text-[20px] font-semibold text-accent mb-4">
              {section.title}
            </h2>
            <div className="font-sans text-[14px] text-content leading-[1.7] space-y-3">
              {section.blocks.map((block, bi) => {
                if ("p" in block) return <p key={bi}>{autoLink(block.p)}</p>;
                if ("sub" in block)
                  return (
                    <div
                      key={bi}
                      className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mt-4 mb-2"
                    >
                      {block.sub}
                    </div>
                  );
                return (
                  <ul key={bi} className="list-disc pl-6 space-y-1">
                    {block.ul.map((item, ii) => (
                      <li key={ii}>
                        {typeof item === "string" ? (
                          autoLink(item)
                        ) : (
                          <>
                            <strong className="text-accent">{item.term}</strong>{" "}
                            {autoLink(item.text)}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                );
              })}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
